/**
 * home-hero.js — Hero slideshow carousel
 *
 * Loaded only when the section renders 2+ slides (the section template
 * gates the <script> tag on `is_carousel`). Single-slide hero sections
 * skip this file entirely.
 *
 * Behaviors:
 *   - Click on dot → goTo(index)
 *   - Click on prev/next arrows → step ±1
 *   - Touch swipe (>50px horizontal travel) → step ±1
 *   - ArrowLeft/ArrowRight when section has focus → step ±1
 *   - Autoplay (opt-in via section setting) advances at the configured
 *     interval. Pauses on hover, focus, or page-hidden visibility.
 *   - prefers-reduced-motion: reduce — autoplay disabled, transitions
 *     handled by CSS (see the @media block in home-hero.css).
 *
 * No framework. Vanilla ES module.
 */

(() => {
  'use strict';

  class HomeHeroCarousel {
    constructor(root) {
      this.root = root;
      this.slides = Array.from(root.querySelectorAll('.home-hero__slide'));
      this.dots = Array.from(root.querySelectorAll('[data-carousel-dot]'));
      this.prevBtn = root.querySelector('[data-carousel-prev]');
      this.nextBtn = root.querySelector('[data-carousel-next]');

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.autoplayEnabled = root.dataset.autoplay === 'true' && !reducedMotion;
      this.intervalMs = (parseInt(root.dataset.autoplayInterval, 10) || 6) * 1000;

      this.current = 0;
      this.timer = null;

      this.bindEvents();
      if (this.autoplayEnabled) this.startAutoplay();
    }

    bindEvents() {
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const idx = parseInt(dot.dataset.slideIndex, 10);
          this.goTo(idx);
        });
      });

      this.prevBtn?.addEventListener('click', () => this.go(-1));
      this.nextBtn?.addEventListener('click', () => this.go(1));

      /* Swipe — only horizontal travel >= 50px counts. Passive listener on
         touchstart so the page can still scroll vertically while the user
         is figuring out which axis they're moving in. */
      let startX = 0;
      this.root.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
      }, { passive: true });
      this.root.addEventListener('touchend', (e) => {
        const diff = startX - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) this.go(diff > 0 ? 1 : -1);
      });

      /* Keyboard arrows when section has keyboard focus. preventDefault so
         the page doesn't scroll horizontally when the carousel handles it. */
      this.root.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft')  { e.preventDefault(); this.go(-1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this.go(1);  }
      });

      /* Pause autoplay when user is interacting or tab is hidden. The
         restart-on-leave keeps the carousel responsive to user attention. */
      this.root.addEventListener('mouseenter', () => this.pauseAutoplay());
      this.root.addEventListener('mouseleave', () => this.startAutoplay());
      this.root.addEventListener('focusin',    () => this.pauseAutoplay());
      this.root.addEventListener('focusout',   () => this.startAutoplay());

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.pauseAutoplay();
        else this.startAutoplay();
      });
    }

    go(delta) {
      this.goTo(this.current + delta);
    }

    goTo(index) {
      const len = this.slides.length;
      this.current = ((index % len) + len) % len;
      this.render();
      /* Restart the autoplay clock on user-driven nav so a click immediately
         before an auto-advance doesn't double-skip. */
      if (this.autoplayEnabled) this.startAutoplay();
    }

    render() {
      this.slides.forEach((slide, i) => {
        const active = i === this.current;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
      });
      this.dots.forEach((dot, i) => {
        const active = i === this.current;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', String(active));
      });
    }

    startAutoplay() {
      if (!this.autoplayEnabled) return;
      this.pauseAutoplay();
      this.timer = setInterval(() => this.go(1), this.intervalMs);
    }

    pauseAutoplay() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    }
  }

  document.querySelectorAll('.home-hero[data-carousel]').forEach((el) => {
    new HomeHeroCarousel(el);
  });
})();
